import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdminLayout } from './components/AdminLayout';
import { API_BASE_URL } from '../../config/apiConfig';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background-color: transparent;
  color: ${props => props.active ? '#3498db' : '#6c757d'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#3498db' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    color: #3498db;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const NoticeBar = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  ${props => props.type === 'success' ? `
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  ` : `
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e9ecef;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #2c3e50;
  vertical-align: middle;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status?.toLowerCase()) {
      case 'success':
      case 'completed':
      case 'active':
        return `
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'pending':
      case 'processing':
        return `
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
      case 'failed':
      case 'cancelled':
      case 'declined':
        return `
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'expired':
        return `
          background-color: #fdf2e9;
          color: #b85450;
          border: 1px solid #fab1a0;
        `;
      default:
        return `
          background-color: #e2e3e5;
          color: #383d41;
          border: 1px solid #d6d8db;
        `;
    }
  }}
`;

const OrderTypeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.type) {
      case 'product':
        return `
          background-color: #f0f9e7;
          color: #2d5a0b;
          border: 1px solid #d4edaa;
        `;
      case 'subscription':
        return `
          background-color: #e7f3ff;
          color: #0066cc;
          border: 1px solid #b3d9ff;
        `;
      default:
        return `
          background-color: #e2e3e5;
          color: #383d41;
          border: 1px solid #d6d8db;
        `;
    }
  }}
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProductImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  background-color: #f8f9fa;
`;

const ProductImagePlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #6c757d;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  flex-direction: column;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #7f8c8d;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  padding: 15px;
  margin: 20px 0;
  color: #c33;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6c757d;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 10px;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background-color: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background-color: #e9ecef;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// API Functions
const fetchOrders = async (orderType = 'product', filters = {}, page = 1, limit = 20) => {
  try {
    const token = sessionStorage.getItem('admin-token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams({
      type: orderType,
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
    });

    const endpoint = orderType === 'product' ? '/admin/product-orders' : (orderType === 'subscription' ? '/admin/subscriptions' : '/admin/invoices');
    const response = await fetch(`${API_BASE_URL}${endpoint}?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      }
    });
    
    const result = await response.json();
    if (result.is_success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Failed to fetch orders');
    }
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

const fetchOrderStats = async (orderType = 'product', filters = {}) => {
  try {
    const token = sessionStorage.getItem('admin-token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams({
      type: orderType,
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
    });

    const endpoint = orderType === 'product' ? '/admin/product-orders/stats' : (orderType === 'subscription' ? '/admin/subscriptions/stats' : '/admin/invoices/stats');
    const response = await fetch(`${API_BASE_URL}${endpoint}?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token
      }
    });
    
    const result = await response.json();
    if (result.is_success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Failed to fetch order stats');
    }
  } catch (error) {
    console.error('Error fetching order stats:', error);
    throw error;
  }
};

const updateProductOrderStatus = async (orderId, orderStatus) => {
  const token = sessionStorage.getItem('admin-token');
  const resp = await fetch(`${API_BASE_URL}/admin/product-orders/${orderId}/status`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token
    },
    body: JSON.stringify({ orderStatus })
  });
  const json = await resp.json();
  if (!json.is_success) {
    throw new Error(json.error || 'Failed to update order status');
  }
  return json.data;
};

export function OrderManagement() {
  const [activeTab, setActiveTab] = useState('product'); // 'product' | 'subscription' | 'invoice'
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [downloadingInvoices, setDownloadingInvoices] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });
  const [notice, setNotice] = useState(null); // { type: 'success' | 'error', message: string }

  const notify = (type, message) => {
    setNotice({ type, message });
    window.clearTimeout(notify._t);
    notify._t = window.setTimeout(() => setNotice(null), 3000);
  };

  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Fetch orders when dependencies change
  useEffect(() => {
    loadOrders();
  }, [activeTab, currentPage, filters]);

  // Fetch stats separately
  useEffect(() => {
    loadStats();
  }, [activeTab, filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchOrders(activeTab, filters, currentPage, 20);
      if (activeTab === 'invoice') {
        setOrders(data.invoices || []);
      } else {
        setOrders(data.orders || []);
      }
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalCount(data.pagination?.totalCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await fetchOrderStats(activeTab, filters);
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadInvoicePdf = async (invoiceNumber, e) => {
    if (e) e.stopPropagation();
    if (!invoiceNumber) return;
    if (downloadingInvoices[invoiceNumber]) return;

    try {
      setDownloadingInvoices(prev => ({ ...prev, [invoiceNumber]: true }));
      const token = sessionStorage.getItem('admin-token');
      const resp = await fetch(`${API_BASE_URL}/admin/invoices/${encodeURIComponent(invoiceNumber)}/pdf`, {
        method: 'GET',
        headers: { 'Authorization': token }
      });
      if (!resp.ok) {
        let msg = `Failed to download PDF (${resp.status})`;
        try {
          const j = await resp.json();
          msg = j?.error || j?.message || msg;
        } catch (_) {}
        throw new Error(msg);
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify('error', err.message || 'Failed to download invoice PDF');
    } finally {
      setDownloadingInvoices(prev => ({ ...prev, [invoiceNumber]: false }));
    }
  };

  const renderProductOrderRow = (order) => (
    <TableRow key={order.orderId}>
      <TableCell>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {order.orderId}
        </div>
      </TableCell>
      <TableCell>
        <div>
          <div style={{ fontWeight: '500' }}>
            {order.user ? `${order.user.firstname || ''} ${order.user.lastname || ''}`.trim() || order.user.email : 'N/A'}
          </div>
          <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
            {order.customerEmail}
          </div>
        </div>
      </TableCell>
      <TableCell>
        {order.product ? (
          <ProductInfo>
            {order.product.images && order.product.images.length > 0 ? (
              <ProductImage src={order.product.images[0]} alt={order.product.name} />
            ) : (
              <ProductImagePlaceholder>IMG</ProductImagePlaceholder>
            )}
            <div>
              <div style={{ fontWeight: '500', fontSize: '13px' }}>
                {order.product.name}
              </div>
              <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
                {order.product.brand} • {order.product.category}
              </div>
            </div>
          </ProductInfo>
        ) : order.orderItems && Array.isArray(order.orderItems) && order.orderItems.length > 0 ? (
          <ProductInfo>
            <ProductImagePlaceholder style={{ background: '#e7f3ff', color: '#0066cc' }}>INV</ProductImagePlaceholder>
            <div>
              <div style={{ fontWeight: '500', fontSize: '13px' }}>
                {order.orderItems[0].productName || order.orderItems[0].name}
                {order.orderItems.length > 1 && (
                  <span style={{ color: '#7f8c8d', fontWeight: '400' }}> (+{order.orderItems.length - 1} more)</span>
                )}
              </div>
              <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
                Manual Invoice • {order.orderItems.length} item{order.orderItems.length > 1 ? 's' : ''}
              </div>
            </div>
          </ProductInfo>
        ) : (
          <div style={{ color: '#7f8c8d' }}>Product not found</div>
        )}
      </TableCell>
      <TableCell>
        <div style={{ fontWeight: '600' }}>
          {formatCurrency(order.amount, order.currency)}
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={order.status}>
          {order.status}
        </StatusBadge>
      </TableCell>
      <TableCell>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FilterSelect
            value={order.orderStatus || 'order placed'}
            onChange={async (e) => {
              const newStatus = e.target.value;
              try {
                await updateProductOrderStatus(order.orderId, newStatus);
                setOrders(prev => prev.map(o => o.orderId === order.orderId ? { ...o, orderStatus: newStatus } : o));
                notify('success', `Order ${order.orderId} updated to "${newStatus}"`);
              } catch (err) {
                notify('error', err.message || 'Failed to update order status');
              }
            }}
          >
            <option value="order placed">Order Placed</option>
            <option value="order confirmed">Order Confirmed</option>
            <option value="order dispatched">Order Dispatched</option>
            <option value="order delivered">Order Delivered</option>
          </FilterSelect>
        </div>
      </TableCell>
      <TableCell>
        <div style={{ fontSize: '12px' }}>
          {formatDate(order.createdAt)}
        </div>
      </TableCell>
      <TableCell>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {order.invoiceNumber || '-'}
        </div>
      </TableCell>
    </TableRow>
  );

  const renderSubscriptionRow = (subscription) => (
    <TableRow key={subscription.subscriptionId}>
      <TableCell>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {subscription.subscriptionId}
        </div>
      </TableCell>
      <TableCell>
        <div>
          <div style={{ fontWeight: '500' }}>
            {subscription.user ? `${subscription.user.firstname || ''} ${subscription.user.lastname || ''}`.trim() || subscription.user.email : 'N/A'}
          </div>
          <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
            {subscription.userId}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div>
          <div style={{ fontWeight: '500' }}>{subscription.planType}</div>
          <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
            {subscription.frequency}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div style={{ fontWeight: '600' }}>
          {formatCurrency(subscription.amount, subscription.currency)}
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={subscription.status}>
          {subscription.status}
        </StatusBadge>
      </TableCell>
      <TableCell>
        <div style={{ fontSize: '12px' }}>
          <div>Started: {formatDate(subscription.createdAt)}</div>
          {subscription.endDate && (
            <div style={{ color: '#7f8c8d' }}>
              Ends: {formatDate(subscription.endDate)}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {subscription.invoiceNumber || '-'}
        </div>
      </TableCell>
    </TableRow>
  );

  const renderInvoiceRow = (inv) => (
    <TableRow key={inv.invoiceNumber} onClick={() => setExpandedInvoices(prev => ({ ...prev, [inv.invoiceNumber]: !prev[inv.invoiceNumber] }))} style={{ cursor: 'pointer' }}>
      <TableCell>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{inv.invoiceNumber}</div>
      </TableCell>
      <TableCell>
        <div>
          <div style={{ fontWeight: '500' }}>
            {inv.user ? `${inv.user.firstname || ''} ${inv.user.lastname || ''}`.trim() || inv.user.email : inv.userId}
          </div>
          <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{inv.user?.email || ''}</div>
        </div>
      </TableCell>
      <TableCell>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{inv.orderId || '-'}</div>
      </TableCell>
      <TableCell>
        <div style={{ fontSize: '12px' }}>
          {inv.subscriptionId ? (
            <>Subscription - {inv.subscription?.planType || inv.details?.subscriptionType || '-'}</>
          ) : inv.details?.items && Array.isArray(inv.details.items) && inv.details.items.length > 0 ? (
            <>
              {inv.details.items[0].name}
              {inv.details.items.length > 1 && (
                <span style={{ color: '#7f8c8d' }}> (+{inv.details.items.length - 1} more)</span>
              )}
            </>
          ) : inv.order?.product?.name ? (
            inv.order.product.name
          ) : inv.order?.orderItems && Array.isArray(inv.order.orderItems) && inv.order.orderItems.length > 0 ? (
            <>
              {inv.order.orderItems[0].productName || inv.order.orderItems[0].name}
              {inv.order.orderItems.length > 1 && (
                <span style={{ color: '#7f8c8d' }}> (+{inv.order.orderItems.length - 1} more)</span>
              )}
            </>
          ) : '-'}
        </div>
      </TableCell>
      <TableCell>
        <div style={{ fontWeight: '600' }}>{formatCurrency(inv.amount, inv.currency)}</div>
      </TableCell>
      <TableCell>
        <div style={{ fontSize: '12px' }}>
          {String(inv.pos || '').trim() === '30' ? 'Intra-State' : (inv.pos ? 'Inter-State' : '-')}
        </div>
      </TableCell>
      <TableCell>
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{inv.pos || '-'}</div>
      </TableCell>
      <TableCell>
        <div style={{ fontSize: '12px' }}>{formatDate(inv.issueDate || inv.createdAt)}</div>
      </TableCell>
    </TableRow>
  );

  const renderInvoiceExpandedRow = (inv) => {
    if (!expandedInvoices[inv.invoiceNumber]) return null;
    const details = inv.details || {};
    const baseAmount = details.amount || inv.amount;
    const gstAmount = details.gst != null ? details.gst : Math.max(0, (details.total || inv.amount) - (details.amount || inv.amount));
    const totalAmount = details.total || inv.amount;
    const method = details.paymentMethodDisplay || details.paymentMethodType || details.paymentMethod || '-';
    const txnId = details.transactionId || '-';
    
    // Get items - handle manual invoices with items array
    const hasItems = details.items && Array.isArray(details.items) && details.items.length > 0;
    const itemName = hasItems 
      ? details.items.map(i => i.name).join(', ')
      : details.subscriptionType || (inv.subscription?.planType) || (inv.order?.product?.name) || '-';

    const safeNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const safePercent = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return null;
      return Math.max(0, Math.min(100, n));
    };
    const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

    const pos = String(inv.pos || '').trim();
    const isIntraState = pos === '30';
    const itemRates = hasItems
      ? details.items.map((i) => safePercent(i.gstRate)).filter((r) => r != null)
      : [];
    const uniqueItemRates = Array.from(new Set(itemRates.map((r) => round2(r))));
    const effectiveRate = safeNum(baseAmount) > 0 ? round2((safeNum(gstAmount) / safeNum(baseAmount)) * 100) : null;
    const singleRate = uniqueItemRates.length === 1 ? uniqueItemRates[0] : (uniqueItemRates.length === 0 ? effectiveRate : null);

    const fmtPct = (n) => {
      if (!Number.isFinite(Number(n))) return '';
      return `${Number(n).toFixed(2).replace(/\.00$/, '')}%`;
    };
    
    return (
      <TableRow key={inv.invoiceNumber + '-expanded'}>
        <TableCell colSpan={8}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <div style={{ fontWeight: 600 }}>Details</div>
                <button
                  type="button"
                  onClick={(e) => downloadInvoicePdf(inv.invoiceNumber, e)}
                  disabled={!!downloadingInvoices[inv.invoiceNumber]}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid #dee2e6',
                    background: '#fff',
                    cursor: downloadingInvoices[inv.invoiceNumber] ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    fontWeight: 600
                  }}
                  title="Download invoice PDF"
                >
                  {downloadingInvoices[inv.invoiceNumber] ? 'Downloading…' : 'Download PDF'}
                </button>
              </div>
              
              {/* Show items table for manual invoices */}
              {hasItems ? (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#495057' }}>Items:</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Item</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Qty</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Rate</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.items.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{item.name}</span>
                            {item.hsn && <span style={{ color: '#7f8c8d', fontSize: 11, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}> (HSN/SAC: {item.hsn})</span>}
                          </td>
                          <td style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.quantity}</td>
                          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{formatCurrency(item.unitPrice, inv.currency || 'INR')}</td>
                          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{formatCurrency(item.total || (item.quantity * item.unitPrice), inv.currency || 'INR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#2c3e50', marginBottom: 8 }}>Item: {itemName}</div>
              )}
              
              <div style={{ fontSize: 13, color: '#2c3e50' }}>Payment Method: {method}</div>
              <div style={{ fontSize: 13, color: '#2c3e50' }}>Transaction ID: {txnId}</div>
              {details.notes && (
                <div style={{ fontSize: 13, color: '#7f8c8d', marginTop: 4 }}>Notes: {details.notes}</div>
              )}
              
              {/* Billing Information */}
              {(details.billingAddress || details.customer) && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Billing</div>
                  {details.billingAddress?.billToCompany && details.billingAddress?.companyName ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{details.billingAddress.companyName}</div>
                      <div style={{ fontSize: 12, color: '#7f8c8d' }}>c/o {details.customer?.name || '-'}</div>
                    </>
                  ) : details.customer?.billToCompany && details.customer?.companyName ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{details.customer.companyName}</div>
                      <div style={{ fontSize: 12, color: '#7f8c8d' }}>c/o {details.customer?.name || '-'}</div>
                    </>
                  ) : (
                    <div style={{ fontSize: 13 }}>{details.customer?.name || '-'}</div>
                  )}
                  {details.customer?.gst && (
                    <div style={{ fontSize: 12, color: '#7f8c8d' }}>GSTIN: {details.customer.gst}</div>
                  )}
                </div>
              )}
               {inv.order && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Order</div>
                  <div style={{ fontSize: 13 }}>Status: {inv.order.status}</div>
                  <div style={{ fontSize: 13 }}>Order Status: {inv.order.orderStatus || '-'}</div>
                  <div style={{ fontSize: 13 }}>Amount: {formatCurrency(inv.order.amount, inv.order.currency || 'INR')}</div>
                </div>
              )}
              {inv.subscription && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Subscription</div>
                  <div style={{ fontSize: 13 }}>Plan: {inv.subscription.planType}</div>
                  <div style={{ fontSize: 13 }}>Status: {inv.subscription.status}</div>
                  <div style={{ fontSize: 13 }}>Amount: {formatCurrency(inv.subscription.amount, inv.subscription.currency || 'INR')}</div>
                </div>
              )}
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>GST Breakup</div>
              {isIntraState ? (
                // Intra-state (Goa POS 30): split CGST/SGST 9% each
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 4px' }}>Base</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 4px' }}>
                        CGST{singleRate != null ? ` (${fmtPct(singleRate / 2)})` : ''}
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 4px' }}>
                        SGST{singleRate != null ? ` (${fmtPct(singleRate / 2)})` : ''}
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 4px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '6px 4px' }}>{formatCurrency(baseAmount, inv.currency || 'INR')}</td>
                      <td style={{ padding: '6px 4px' }}>{formatCurrency((Number(gstAmount) || 0) / 2, inv.currency || 'INR')}</td>
                      <td style={{ padding: '6px 4px' }}>{formatCurrency((Number(gstAmount) || 0) / 2, inv.currency || 'INR')}</td>
                      <td style={{ padding: '6px 4px' }}>{formatCurrency(totalAmount, inv.currency || 'INR')}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 4px' }}>Base</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 4px' }}>
                        IGST{singleRate != null ? ` (${fmtPct(singleRate)})` : (uniqueItemRates.length > 1 ? ' (varies)' : '')}
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #e9ecef', padding: '6px 4px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '6px 4px' }}>{formatCurrency(baseAmount, inv.currency || 'INR')}</td>
                      <td style={{ padding: '6px 4px' }}>{formatCurrency(gstAmount, inv.currency || 'INR')}</td>
                      <td style={{ padding: '6px 4px' }}>{formatCurrency(totalAmount, inv.currency || 'INR')}</td>
                    </tr>
                  </tbody>
                </table>
              )}

              {hasItems && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#495057' }}>Item-wise GST</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Item</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Taxable</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Rate</th>
                        {isIntraState ? (
                          <>
                            <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>CGST</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>SGST</th>
                          </>
                        ) : (
                          <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>IGST</th>
                        )}
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e9ecef' }}>Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.items.map((item, idx) => {
                        const qty = safeNum(item.quantity) || 1;
                        const unitPriceBase = safeNum(item.unitPrice);
                        const baseLine = round2(safeNum(item.total != null ? item.total : (qty * unitPriceBase)));

                        const r = safePercent(item.gstRate) ?? singleRate;
                        const gstLine = item.gst != null
                          ? round2(safeNum(item.gst))
                          : (r != null ? round2(baseLine * (r / 100)) : 0);

                        const lineTotal = round2(baseLine + gstLine);
                        const halfRate = r != null ? r / 2 : null;
                        const halfTax = round2(gstLine / 2);

                        return (
                          <tr key={idx}>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                              {item.name || '-'}
                            </td>
                            <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                              {formatCurrency(baseLine, inv.currency || 'INR')}
                            </td>
                            <td style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                              {r != null ? fmtPct(r) : '-'}
                            </td>
                            {isIntraState ? (
                              <>
                                <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                  <div>{formatCurrency(halfTax, inv.currency || 'INR')}</div>
                                  <div style={{ fontSize: 10, color: '#7f8c8d' }}>{halfRate != null ? fmtPct(halfRate) : ''}</div>
                                </td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                  <div>{formatCurrency(halfTax, inv.currency || 'INR')}</div>
                                  <div style={{ fontSize: 10, color: '#7f8c8d' }}>{halfRate != null ? fmtPct(halfRate) : ''}</div>
                                </td>
                              </>
                            ) : (
                              <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                <div>{formatCurrency(gstLine, inv.currency || 'INR')}</div>
                                <div style={{ fontSize: 10, color: '#7f8c8d' }}>{r != null ? fmtPct(r) : ''}</div>
                              </td>
                            )}
                            <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                              {formatCurrency(lineTotal, inv.currency || 'INR')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>Order Management</PageTitle>
      </PageHeader>

      {notice && (
        <NoticeBar type={notice.type}>
          {notice.message}
        </NoticeBar>
      )}

      {/* Tabs */}
      <TabContainer>
        <Tab 
          active={activeTab === 'product'} 
          onClick={() => setActiveTab('product')}
        >
          Product Orders
        </Tab>
        <Tab 
          active={activeTab === 'subscription'} 
          onClick={() => setActiveTab('subscription')}
        >
          Subscriptions
        </Tab>
        <Tab 
          active={activeTab === 'invoice'} 
          onClick={() => setActiveTab('invoice')}
        >
          Invoices
        </Tab>
        {activeTab === 'invoice' && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <PaginationButton onClick={async () => {
              try {
                const token = sessionStorage.getItem('admin-token');
                const queryParams = new URLSearchParams({
                  startDate: filters.startDate || '',
                  endDate: filters.endDate || '',
                  searchTerm: filters.searchTerm || ''
                });
                const resp = await fetch(`${API_BASE_URL}/admin/invoices/export?${queryParams}`, { headers: { 'Authorization': token } });
                const blob = await resp.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'invoices.csv'; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
              } catch (e) { console.error('Export failed', e); }
            }}>Export CSV</PaginationButton>
          </div>
        )}
      </TabContainer>

      {/* Stats Cards */}
      {stats && (
        <StatsContainer>
          <StatCard>
            <StatValue>{formatCurrency(stats.totalRevenue || 0)}</StatValue>
            <StatLabel>
              {activeTab === 'invoice' ? 'Gross Revenue' : 'Paid Revenue'}
            </StatLabel>
            {activeTab !== 'invoice' && (stats.createdRevenue != null) && (
              <div style={{ fontSize: 11, color: '#7f8c8d', marginTop: 6 }}>
                Created revenue: {formatCurrency(stats.createdRevenue || 0)}
              </div>
            )}
          </StatCard>
          {activeTab === 'invoice' ? (
            <>
              <StatCard>
                <StatValue>{formatCurrency(stats.baseRevenue || 0)}</StatValue>
                <StatLabel>Base (Taxable)</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{formatCurrency(stats.gstRevenue || 0)}</StatValue>
                <StatLabel>GST</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{formatCurrency(stats.productOrderRevenue || 0)}</StatValue>
                <StatLabel>PO Revenue</StatLabel>
                {(stats.productOrderBaseRevenue != null || stats.productOrderGstRevenue != null) && (
                  <div style={{ fontSize: 11, color: '#7f8c8d', marginTop: 6 }}>
                    Base: {formatCurrency(stats.productOrderBaseRevenue || 0)} • GST: {formatCurrency(stats.productOrderGstRevenue || 0)}
                  </div>
                )}
              </StatCard>
              <StatCard>
                <StatValue>{formatCurrency(stats.subscriptionRevenue || 0)}</StatValue>
                <StatLabel>Sub Revenue</StatLabel>
                {(stats.subscriptionBaseRevenue != null || stats.subscriptionGstRevenue != null) && (
                  <div style={{ fontSize: 11, color: '#7f8c8d', marginTop: 6 }}>
                    Base: {formatCurrency(stats.subscriptionBaseRevenue || 0)} • GST: {formatCurrency(stats.subscriptionGstRevenue || 0)}
                  </div>
                )}
              </StatCard>
              <StatCard>
                <StatValue>{stats.totalInvoices || 0}</StatValue>
                <StatLabel>Total Invoices</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.totalOrders || 0}</StatValue>
                <StatLabel>Total Orders</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.totalSubscriptions || 0}</StatValue>
                <StatLabel>Total Subscriptions</StatLabel>
              </StatCard>
            </>
          ) : (
            <>
              <StatCard>
                <StatValue>{stats.totalOrders || 0}</StatValue>
                <StatLabel>Total {activeTab === 'product' ? 'Orders' : 'Subscriptions'}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.completedOrders || 0}</StatValue>
                <StatLabel>PaymentCompleted</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.pendingOrders || 0}</StatValue>
                <StatLabel>Payment Pending</StatLabel>
              </StatCard>
              {activeTab === 'product' && (
                <>
                  <StatCard style={{ cursor: 'pointer' }} onClick={() => handleFilterChange('orderStatus', 'order placed')}>
                    <StatValue>{stats.orderStatusCounts?.orderPlaced || 0}</StatValue>
                    <StatLabel>Order Placed</StatLabel>
                  </StatCard>
                  <StatCard style={{ cursor: 'pointer' }} onClick={() => handleFilterChange('orderStatus', 'order confirmed')}>
                    <StatValue>{stats.orderStatusCounts?.orderConfirmed || 0}</StatValue>
                    <StatLabel>Order Confirmed</StatLabel>
                  </StatCard>
                  <StatCard style={{ cursor: 'pointer' }} onClick={() => handleFilterChange('orderStatus', 'order dispatched')}>
                    <StatValue>{stats.orderStatusCounts?.orderDispatched || 0}</StatValue>
                    <StatLabel>Order Dispatched</StatLabel>
                  </StatCard>
                  <StatCard style={{ cursor: 'pointer' }} onClick={() => handleFilterChange('orderStatus', 'order delivered')}>
                    <StatValue>{stats.orderStatusCounts?.orderDelivered || 0}</StatValue>
                    <StatLabel>Order Delivered</StatLabel>
                  </StatCard>
                </>
              )}
              {activeTab === 'subscription' && (
                <StatCard>
                  <StatValue>{stats.activeSubscriptions || 0}</StatValue>
                  <StatLabel>Active</StatLabel>
                </StatCard>
              )}
            </>
          )}
        </StatsContainer>
      )}

      {/* Filters */}
      <FiltersContainer>
        {activeTab !== 'invoice' && (
        <FilterGroup>
          <FilterLabel>Payment Status</FilterLabel>
          <FilterSelect 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            {activeTab === 'product' ? (
              <>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </>
            ) : (
              <>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </>
            )}
          </FilterSelect>
        </FilterGroup>
        )}

        {activeTab === 'product' && (
        <FilterGroup>
          <FilterLabel>Order Status</FilterLabel>
          <FilterSelect 
            value={filters.orderStatus}
            onChange={(e) => handleFilterChange('orderStatus', e.target.value)}
          >
            <option value="">All Order Statuses</option>
            <option value="order placed">Order Placed</option>
            <option value="order confirmed">Order Confirmed</option>
            <option value="order dispatched">Order Dispatched</option>
            <option value="order delivered">Order Delivered</option>
          </FilterSelect>
        </FilterGroup>
        )}

        <FilterGroup>
          <FilterLabel>Start Date</FilterLabel>
          <FilterInput 
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>End Date</FilterLabel>
          <FilterInput 
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Search</FilterLabel>
          <FilterInput 
            type="text"
            placeholder={`${activeTab === 'product' ? 'Order' : activeTab === 'subscription' ? 'Subscription' : 'Invoice'} ID, User ID, Email...`}
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          />
        </FilterGroup>
      </FiltersContainer>

      {error && (
        <ErrorContainer>
          <div>Error: {error}</div>
        </ErrorContainer>
      )}

      {/* Orders Table */}
      <TableContainer>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
      <LoadingText>Loading {activeTab === 'invoice' ? 'invoices' : `${activeTab} orders`}...</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  {activeTab === 'invoice' ? (
                    <>
                      <TableHeader>Invoice No.</TableHeader>
                      <TableHeader>Customer</TableHeader>
                      <TableHeader>Order ID</TableHeader>
                      <TableHeader>Item</TableHeader>
                      <TableHeader>Amount</TableHeader>
                      <TableHeader>Tax</TableHeader>
                      <TableHeader>POS</TableHeader>
                      <TableHeader>Issue Date</TableHeader>
                    </>
                  ) : (
                    <>
                      <TableHeader>{activeTab === 'product' ? 'Order' : 'Subscription'} ID</TableHeader>
                      <TableHeader>Customer</TableHeader>
                      <TableHeader>{activeTab === 'product' ? 'Product' : 'Plan'}</TableHeader>
                      <TableHeader>Amount</TableHeader>
                      <TableHeader>Payment Status</TableHeader>
                      {activeTab === 'product' && (
                        <TableHeader>Order Status</TableHeader>
                      )}
                      <TableHeader>Date</TableHeader>
                      <TableHeader>Invoice No.</TableHeader>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {orders.map(row => (
                  activeTab === 'product' ? (
                    renderProductOrderRow(row)
                  ) : activeTab === 'subscription' ? (
                    renderSubscriptionRow(row)
                  ) : (
                    <>
                      {renderInvoiceRow(row)}
                      {renderInvoiceExpandedRow(row)}
                    </>
                  )
                ))}
              </tbody>
            </Table>

            {orders.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                No {activeTab} orders found matching the current filters.
              </div>
            )}

            <Pagination>
            <PaginationInfo>
              Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} {activeTab === 'invoice' ? 'invoices' : `${activeTab} orders`}
            </PaginationInfo>
              <PaginationControls>
                <PaginationButton 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </PaginationButton>
                <span style={{ padding: '8px 12px', fontSize: '14px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <PaginationButton 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationControls>
            </Pagination>
          </>
        )}
      </TableContainer>
    </AdminLayout>
  );
} 